import React from 'react';
import {
    Box, Grid, Typography, makeStyles
} from '@material-ui/core';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    container: {
        marginBottom: theme.spacing(3),
    },
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 160,
    },
    cardLabel: {
        fontSize: '10px',
        fontWeight: 700,
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: theme.spacing(1.5),
        textAlign: 'center',
    },
    bigNumber: {
        fontSize: '38px',
        fontWeight: 700,
        color: '#374151',
        lineHeight: 1,
    },
    gaugeContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    gaugeLabel: {
        position: 'absolute',
        fontSize: '18px',
        fontWeight: 700,
        color: '#374151',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.7)',
        zIndex: 2,
        borderRadius: '8px',
        '& .MuiCircularProgress-colorPrimary': {
            color: '#6366F1',
        },
    },
}));

const DonutGauge = ({ value, max = 100, size = 90, strokeWidth = 8, color }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const percent = Math.min(value / max, 1);
    const offset = circumference * (1 - percent);

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#F3F4F6"
                strokeWidth={strokeWidth}
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
        </svg>
    );
};

const EMPTY_SUMMARY = { totalReviews: 0, avgSentiment: 0, positivePercent: 0, negativePercent: 0 };

const MetricsSection = ({ summary, isLoading }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const data = summary || EMPTY_SUMMARY;

    return (
        <Box className={classes.container} style={{ position: 'relative' }}>
            {isLoading && (
                <Box className={classes.loadingOverlay}>
                    <AtCircularProgress />
                </Box>
            )}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.card}>
                        <Typography className={classes.cardLabel}>{t('sentiment.metrics.totalReviews')}</Typography>
                        <Typography className={classes.bigNumber}>{data.totalReviews}</Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.card}>
                        <Typography className={classes.cardLabel}>{t('sentiment.metrics.avgSentiment')}</Typography>
                        <Box className={classes.gaugeContainer}>
                            <DonutGauge
                                value={data.avgSentiment}
                                max={5}
                                size={96}
                                strokeWidth={9}
                                color="#4F46E5"
                            />
                            <Typography className={classes.gaugeLabel}>
                                {data.avgSentiment}/5
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.card}>
                        <Typography className={classes.cardLabel}>{t('sentiment.metrics.positivePercentage')}</Typography>
                        <Box className={classes.gaugeContainer}>
                            <DonutGauge
                                value={data.positivePercent}
                                max={100}
                                size={96}
                                strokeWidth={9}
                                color="#10B981"
                            />
                            <Typography className={classes.gaugeLabel}>
                                {data.positivePercent}%
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.card}>
                        <Typography className={classes.cardLabel}>{t('sentiment.metrics.negativePercentage')}</Typography>
                        <Box className={classes.gaugeContainer}>
                            <DonutGauge
                                value={data.negativePercent}
                                max={100}
                                size={96}
                                strokeWidth={9}
                                color="#EF4444"
                            />
                            <Typography className={classes.gaugeLabel}>
                                {data.negativePercent}%
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MetricsSection;
